import { useState, useEffect } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { GoogleLogo, Copy, ShareNetwork, Star, CheckCircle } from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { PHONE_DISPLAY, PHONE_TEL } from "@/seoData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_URL = "https://search.google.com/local/writereview?placeid=ChIJx8Eov0VYD6wRPjfrK_i3LIE";

export default function ReviewBooster() {
  const [url, setUrl] = useState(FALLBACK_URL);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/review-link`)
      .then(({ data }) => data?.url && setUrl(data.url))
      .catch(() => {});
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Review link copied — paste it in a text to your customer.");
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy it manually.");
    }
  };

  const share = async () => {
    const shareData = {
      title: "Leave CleanOut247 a review",
      text: "Thanks for choosing CleanOut247! Would you leave us a quick Google review?",
      url,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <div className="bg-white text-[#0A0A0A] overflow-x-hidden" data-testid="review-booster-page">
      <Helmet>
        <title>Leave CleanOut247 a Google Review</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <Header />

      <section className="pt-28 sm:pt-40 lg:pt-48 pb-14 px-5 lg:px-8 bg-[#0B1E45] text-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center gap-2 text-[#5DB82F] font-display font-bold uppercase tracking-widest text-xs mb-4">
            <span className="h-px w-8 bg-[#5DB82F]" /> Thank You <span className="h-px w-8 bg-[#5DB82F]" />
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.98]">
            Enjoyed the service?<br />Leave us a review.
          </h1>
          <p className="mt-5 text-lg text-white/80 max-w-xl mx-auto leading-relaxed">
            It takes about 30 seconds and it truly helps our small local business.
            Scan the code or tap the button below.
          </p>
        </div>
      </section>

      <section className="px-5 lg:px-8 -mt-8 pb-20">
        <div className="max-w-md mx-auto bg-white rounded-sm border-t-4 border-[#46A01E] shadow-2xl p-8 text-center">
          <div
            data-testid="review-qr"
            className="mx-auto w-fit bg-white p-4 rounded-sm border border-[#E4E4E7]"
          >
            <QRCodeSVG value={url} size={220} level="M" fgColor="#0B1E45" includeMargin={false} />
          </div>
          <p className="text-sm text-[#52525B] mt-4">
            Point your phone camera at the code to open our Google review page.
          </p>

          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            data-testid="review-google-button"
            className="mt-6 w-full flex items-center justify-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm py-4 rounded-sm transition-colors"
          >
            <GoogleLogo size={20} weight="bold" /> Leave a Google Review
          </a>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <button
              onClick={share}
              data-testid="review-share-button"
              className="flex items-center justify-center gap-2 border border-[#E4E4E7] hover:border-[#46A01E] text-[#15306E] font-display font-bold text-sm py-3 rounded-sm transition-colors"
            >
              <ShareNetwork size={18} weight="bold" /> Share
            </button>
            <button
              onClick={copy}
              data-testid="review-copy-button"
              className="flex items-center justify-center gap-2 border border-[#E4E4E7] hover:border-[#46A01E] text-[#15306E] font-display font-bold text-sm py-3 rounded-sm transition-colors"
            >
              <Copy size={18} weight="bold" /> Copy link
            </button>
          </div>

          <div className="mt-6 pt-5 border-t border-[#E4E4E7] flex items-center justify-center gap-1 text-[#5DB82F]">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={22} weight="fill" />
            ))}
          </div>
        </div>

        <div className="max-w-md mx-auto mt-8 space-y-2.5">
          {[
            "Tap “Leave a Google Review” (or scan the code).",
            "Pick your star rating and write a quick line.",
            "Hit post — that’s it. Thank you!",
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3 text-sm text-[#3F3F46]">
              <CheckCircle size={20} weight="fill" className="text-[#46A01E] shrink-0 mt-0.5" />
              <span>{step}</span>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-[#71717A] mt-8">
          Something not right?{" "}
          <a href={`tel:${PHONE_TEL}`} className="text-[#46A01E] font-semibold hover:underline">
            Call or text us at {PHONE_DISPLAY}
          </a>{" "}
          and we’ll make it right.
        </p>
      </section>

      <Footer />
    </div>
  );
}
